import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import kalibreFontYukle from '../../utils/kalibreFont';

/**
 * Girişten sonra Sistem Seç açılışı (kullanıcı, 2026-10-03): "Yetsan logosu lazerle kazınıyor gibi,
 * sonra ekranın ortasından sayfa yukarı ve aşağı ikiye ayrılıp Sistem Seç'e gelsin." Müşteri girişinde de
 * (2026-10-05: "müşteri paneli de öyle açılsın") müşteri paneli aynı açılışla gelir.
 *
 * 1) Fırçalanmış alüminyum plaka (radyatörün malzemesi). Lazer kafası raster tarar gibi zikzak çizerek
 *    soldan sağa ilerler; arkasında logo kırmızı kazınır, kenarı bir an akkor parlar, kıvılcım saçar.
 * 2) Lazer kalkar; "hoş geldiniz" ve milimetre cetveli belirir.
 * 3) Ortadan yatay bir lazer çizgisi geçer; plaka üst ve alt yarıya ayrılıp açılır, altındaki Sistem
 *    Seç görünür.
 * Tıklama / tuş ile atlanır. Hareket azaltma tercihinde kısa bir solma. Her karede React render
 * edilmez: lazer, kazıma ve kıvılcımlar tek requestAnimationFrame döngüsünde DOM'a yazılır.
 */
const T_BASLA = 250;        // plaka belirdikten sonra lazer başlar
const T_KAZI = 1850;        // kazıma süresi (kullanıcının ayarı: 2,7 → 2,2 → 1,85 sn)
const T_SOGU = 400;         // lazer kalkar, yazı ve cetvel belirir
const T_BEKLE = 150;        // son hal bir an görünür
const T_CIZGI = 220;        // ortadan ayırma çizgisi
const T_AYRIL = 650;        // yarılar açılır
// Kıvılcımların sönmesi BEKLENMEZ (eskiden bekleniyordu: yazı bittikten sonra ~2,8 sn boşa
// geçiyordu); kalan kıvılcımlar açılan plakanın üstünde uçmaya devam eder.
const LOGO = '/yetsan-logo-kirpik.png';
export const GIRIS_INTRO_ANAHTAR = 'yetsan_giris_intro';

const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

const PLAKA_ZEMIN = {
  background: [
    'radial-gradient(ellipse at 50% 42%, rgba(255,255,255,.7), rgba(255,255,255,0) 62%)',
    'repeating-linear-gradient(90deg, rgba(255,255,255,.22) 0 1px, rgba(14,58,70,.028) 1px 3px)',
    'linear-gradient(180deg, #E9EEF0 0%, #D2DADD 56%, #E5EAEC 100%)',
  ].join(','),
};

function Kose({ konum }) {
  const k = { tl: 'top-6 left-6 border-l border-t', tr: 'top-6 right-6 border-r border-t',
    bl: 'bottom-6 left-6 border-l border-b', br: 'bottom-6 right-6 border-r border-b' }[konum];
  return <span aria-hidden="true" className={`absolute h-6 w-6 border-[#0E3A46]/25 ${k}`} />;
}

/** Plakanın son hali — ayrılan yarılarda birebir aynı yerleşimle tekrar çizilir (dikiş görünmesin). */
function PlakaIcerik({ ad, logoRef, kaziRef, sicakRef, yaziRef, cetvelRef, canli }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center" style={PLAKA_ZEMIN}>
      <Kose konum="tl" /><Kose konum="tr" /><Kose konum="bl" /><Kose konum="br" />
      <div ref={logoRef} className="relative" style={{ width: 'min(560px, 72vw)' }}>
        {/* şablon: kazınacak alan silik görünür */}
        <img src={LOGO} alt="" draggable="false" className="block w-full select-none"
          style={{ opacity: canli ? 0.07 : 0, filter: 'grayscale(1)' }} />
        {/* kazınmış logo — hafif kabartma gölgeyle metale işlenmiş görünür */}
        <img ref={kaziRef} src={LOGO} alt="YETSAN" draggable="false" className="absolute inset-0 block w-full select-none"
          style={{ clipPath: canli ? 'inset(0 100% 0 0)' : 'none',
            filter: 'drop-shadow(0 1px 0 rgba(255,255,255,.65)) drop-shadow(0 -1px 0 rgba(11,37,35,.22))' }} />
        {/* akkor kenar — lazerin hemen arkası */}
        {canli && (
          <img ref={sicakRef} src={LOGO} alt="" draggable="false" className="absolute inset-0 block w-full select-none"
            style={{ clipPath: 'inset(0 100% 0 0)', mixBlendMode: 'screen',
              filter: 'brightness(2.1) saturate(1.6) drop-shadow(0 0 6px #ff8a3d) drop-shadow(0 0 16px #ff3b1f)' }} />
        )}
      </div>
      <div ref={yaziRef} className="mt-8 flex flex-col items-center" style={{ opacity: canli ? 0 : 1 }}>
        <span className="text-[12px] tracking-[0.32em] text-[#0E3A46]"
          style={{ fontFamily: "'Martian Mono', ui-monospace, monospace", fontStretch: '112%', fontWeight: 500, marginRight: '-0.32em' }}>
          {ad ? `HOŞ GELDİNİZ, ${ad.toLocaleUpperCase('tr-TR')}` : 'HOŞ GELDİNİZ'}
        </span>
        <div ref={cetvelRef} aria-hidden="true" className="mes-cetvel mt-3"
          style={{ '--mes-rule': '#7F9C98', width: 'min(560px, 72vw)', transformOrigin: 'center', transform: canli ? 'scaleX(0)' : 'none' }} />
      </div>
    </div>
  );
}

export default function GirisIntro({ ad, onBitti }) {
  const [faz, setFaz] = useState('kazi');        // kazi → yaril → bitti
  const [boy, setBoy] = useState(() => window.innerHeight);
  const logoRef = useRef(null);
  const kaziRef = useRef(null);
  const sicakRef = useRef(null);
  const yaziRef = useRef(null);
  const cetvelRef = useRef(null);
  const kafaRef = useRef(null);
  const isinRef = useRef(null);
  const kivilcimRef = useRef(null);
  const cizgiRef = useRef(null);
  const ustRef = useRef(null);
  const altRef = useRef(null);
  const azalt = useRef(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches).current;

  const fazRef = useRef(faz);
  fazRef.current = faz;
  const ayril = useCallback(() => setFaz((f) => (f === 'kazi' ? 'yaril' : f)), []);

  useEffect(() => {
    kalibreFontYukle();
    const r = () => setBoy(window.innerHeight);
    window.addEventListener('resize', r);
    const atla = () => ayril();
    window.addEventListener('keydown', atla);
    const eski = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    return () => { window.removeEventListener('resize', r); window.removeEventListener('keydown', atla);
      document.documentElement.style.overflow = eski; };
  }, [ayril]);

  // 1-2) Kazıma: lazer kafası + ışın + akkor kenar + kıvılcımlar (tek döngü; ayrılma sırasında da
  // kıvılcımları uçurmaya devam eder)
  useEffect(() => {
    if (azalt) { const t = setTimeout(ayril, 1400); return () => clearTimeout(t); }
    const cv = kivilcimRef.current;
    const ctx = cv.getContext('2d');
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = window.innerWidth * dpr; cv.height = window.innerHeight * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const parcalar = [];
    let raf = 0; let son = 0; let sonPatlama = 0; let birikim = 0;
    const olcek = Math.max(0.55, Math.min(1.2, window.innerWidth / 1300));   // telefonda ekrandan taşmasın
    const t0 = performance.now();
    const kare = (now) => {
      const dt = son ? Math.min(0.05, (now - son) / 1000) : 0;
      son = now;
      const gecen = now - t0;
      const p = Math.min(1, Math.max(0, (gecen - T_BASLA) / T_KAZI));
      const x = ease(p);
      const kutu = logoRef.current?.getBoundingClientRect();
      const kaziyor = fazRef.current === 'kazi';
      const aktif = kaziyor && gecen >= T_BASLA && p < 1;
      if (kutu && kaziyor) {
        const sag = (1 - x) * 100;
        if (kaziRef.current) kaziRef.current.style.clipPath = `inset(0 ${sag}% 0 0)`;
        if (sicakRef.current) {
          sicakRef.current.style.clipPath = `inset(0 ${sag}% 0 ${Math.max(0, x * 100 - 6)}%)`;
          sicakRef.current.style.opacity = p >= 1 ? '0' : '1';
        }
        // raster tarama: kafa ilerlerken logonun yüksekliği boyunca zikzak çizer
        const hx = kutu.left + x * kutu.width;
        const hy = kutu.top + kutu.height * (0.5 + 0.44 * Math.sin(gecen / 1000 * Math.PI * 2 * 7));
        if (kafaRef.current) {
          kafaRef.current.style.transform = `translate(${hx - 5}px, ${hy - 5}px)`;
          kafaRef.current.style.opacity = aktif ? '1' : '0';
        }
        if (isinRef.current) {
          isinRef.current.style.transform = `translateX(${hx - 1}px)`;
          isinRef.current.style.height = `${hy}px`;
          isinRef.current.style.opacity = aktif ? '1' : '0';
        }
        if (aktif) {
          // kıvılcımlar etrafa saçılır: iki yana geniş açıyla (yataydan −60° yukarı ile +35° aşağı),
          // sürekli serpinti + arada bir patlama; dörtte biri iri ve uzağa giden. Üretim saniye
          // bazında — 120 Hz ekranda iki katı kıvılcım çıkmasın
          const patla = now - sonPatlama > 200 + Math.random() * 180;
          if (patla) sonPatlama = now;
          birikim += 330 * dt;
          const adet = Math.floor(birikim) + (patla ? 18 : 0);
          birikim -= Math.floor(birikim);
          for (let i = 0; i < adet; i += 1) {
            const yan = Math.random() < 0.5 ? -1 : 1;
            const egim = -1.05 + Math.random() * 1.65;
            const a = yan > 0 ? egim : Math.PI - egim;
            const iri = Math.random() < (patla ? 0.4 : 0.25);
            const v = ((iri ? 520 : 240) + Math.random() * (iri ? 680 : 420)) * olcek;
            parcalar.push({ x: hx, y: hy, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 80 * olcek, o: 1,
              o0: (iri ? 1.0 : 0.5) + Math.random() * (iri ? 0.8 : 0.6), k: iri ? 3.4 : 2 });
          }
        }
      }
      // kıvılcımlar: beyazdan turuncuya, kırmızıya; yerçekimiyle düşer
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      ctx.lineCap = 'round';
      const zemin = window.innerHeight - 3;
      for (let i = parcalar.length - 1; i >= 0; i -= 1) {
        const s = parcalar[i];
        s.o -= dt / s.o0;
        if (s.o <= 0) { parcalar.splice(i, 1); continue; }
        s.vy += 520 * dt; s.vx *= 1 - 0.45 * dt; s.x += s.vx * dt; s.y += s.vy * dt;
        if (s.y > zemin && s.vy > 0) { s.y = zemin; s.vy *= -0.35; s.vx *= 0.7; }   // yere vurup seker
        const g = Math.round(205 * s.o + 35);
        const ix = s.x - s.vx * 0.032; const iy = s.y - s.vy * 0.032;
        // önce geniş, soluk hare; sonra akkor çekirdek
        ctx.strokeStyle = `rgba(255,${Math.round(g * 0.6)},40,${s.o * 0.22})`;
        ctx.lineWidth = s.k * 3.2;
        ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(ix, iy); ctx.stroke();
        ctx.strokeStyle = `rgba(255,${g},${Math.round(110 * s.o)},${Math.min(1, s.o * 1.4)})`;
        ctx.lineWidth = s.k;
        ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.lineTo(ix, iy); ctx.stroke();
      }
      // soğuma: yazı ve cetvel belirir
      const sogu = Math.min(1, Math.max(0, (gecen - T_BASLA - T_KAZI) / T_SOGU));
      if (yaziRef.current) yaziRef.current.style.opacity = String(sogu);
      if (cetvelRef.current) cetvelRef.current.style.transform = `scaleX(${ease(sogu)})`;
      if (kaziyor && gecen >= T_BASLA + T_KAZI + T_SOGU + T_BEKLE) ayril();
      if (kaziyor || parcalar.length) raf = requestAnimationFrame(kare);
    };
    raf = requestAnimationFrame(kare);
    return () => cancelAnimationFrame(raf);
  }, [azalt, ayril]);

  // 3) Ayrılma: ortadan lazer çizgisi, sonra üst ve alt yarı açılır
  useEffect(() => {
    if (faz !== 'yaril') return undefined;
    const c = cizgiRef.current; const u = ustRef.current; const a = altRef.current;
    if (azalt) {
      [u, a].forEach((el) => { if (el) { el.style.transition = 'opacity 400ms ease'; el.style.opacity = '0'; } });
      const t = setTimeout(() => { setFaz('bitti'); onBitti?.(); }, 420);
      return () => clearTimeout(t);
    }
    requestAnimationFrame(() => {
      if (c) { c.style.transition = `transform ${T_CIZGI}ms cubic-bezier(.2,.8,.2,1), opacity 200ms ease ${T_CIZGI + 120}ms`;
        c.style.transform = 'translateY(-50%) scaleX(1)'; c.style.opacity = '0'; }
      [[u, '-100%'], [a, '100%']].forEach(([el, y]) => {
        if (!el) return;
        el.style.transition = `transform ${T_AYRIL}ms cubic-bezier(.76,0,.24,1) ${T_CIZGI}ms`;
        el.style.transform = `translateY(${y})`;
      });
    });
    const t = setTimeout(() => { setFaz('bitti'); onBitti?.(); }, T_CIZGI + T_AYRIL + 60);
    return () => clearTimeout(t);
  }, [faz, azalt, onBitti]);

  if (faz === 'bitti') return null;

  return (
    <div className="fixed inset-0 z-[100001] cursor-pointer select-none" onClick={ayril} role="presentation" aria-hidden="true">
      {faz === 'kazi' ? (
        <div className="absolute inset-0">
          {/* Hareketi azalt tercihinde lazer yok: logo ve karşılama baştan net görünür, kısa solmayla açılır
              (eskiden kazınmamış — neredeyse boş — plaka görünüp sönüyordu, açılış "gelmedi" sanılıyordu) */}
          <PlakaIcerik ad={ad} canli={!azalt} logoRef={logoRef} kaziRef={kaziRef} sicakRef={sicakRef} yaziRef={yaziRef} cetvelRef={cetvelRef} />
          {/* lazer ışını (yukarıdan kafaya) + kafa */}
          <div ref={isinRef} className="pointer-events-none absolute left-0 top-0 w-[2px] opacity-0"
            style={{ background: 'linear-gradient(180deg, rgba(255,90,60,0) 0%, rgba(255,90,60,.55) 70%, #fff 100%)',
              boxShadow: '0 0 8px 2px rgba(255,70,40,.45)' }} />
          <div ref={kafaRef} className="pointer-events-none absolute left-0 top-0 h-[10px] w-[10px] rounded-full bg-white opacity-0"
            style={{ boxShadow: '0 0 6px 3px #fff, 0 0 18px 8px rgba(255,120,60,.85), 0 0 40px 18px rgba(227,36,26,.45)' }} />
        </div>
      ) : (
        <>
          {/* üst yarı — plakanın üst yarısı */}
          <div ref={ustRef} className="absolute left-0 top-0 w-full overflow-hidden will-change-transform"
            style={{ height: boy / 2, boxShadow: '0 10px 28px -10px rgba(11,37,35,.35)' }}>
            <div className="absolute left-0 top-0 w-full" style={{ height: boy }}>
              <PlakaIcerik ad={ad} />
            </div>
            <span className="absolute bottom-0 left-0 h-px w-full bg-white/70" />
          </div>
          {/* alt yarı */}
          <div ref={altRef} className="absolute bottom-0 left-0 w-full overflow-hidden will-change-transform"
            style={{ height: boy / 2, boxShadow: '0 -10px 28px -10px rgba(11,37,35,.35)' }}>
            <div className="absolute bottom-0 left-0 w-full" style={{ height: boy }}>
              <PlakaIcerik ad={ad} />
            </div>
            <span className="absolute left-0 top-0 h-px w-full bg-[#0B2523]/15" />
          </div>
          {/* ortadan ayıran lazer çizgisi */}
          <div ref={cizgiRef} className="pointer-events-none absolute left-0 top-1/2 h-[2px] w-full"
            style={{ transform: 'translateY(-50%) scaleX(0)', transformOrigin: 'center',
              background: 'linear-gradient(90deg, rgba(255,90,60,0), #fff 18%, #fff 82%, rgba(255,90,60,0))',
              boxShadow: '0 0 10px 3px rgba(255,90,50,.75), 0 0 28px 8px rgba(227,36,26,.35)' }} />
        </>
      )}
      {/* kıvılcımlar en üstte: plaka açılırken de uçmaya devam eder */}
      <canvas ref={kivilcimRef} className="pointer-events-none absolute inset-0 h-full w-full" />
    </div>
  );
}

/** Giriş ekranı her başarılı girişte (personel ve müşteri) işaret koyar; işaret bir kez okunur, açılış bir kez oynar. */
export function girisIntroIsaretle() {
  try { sessionStorage.setItem(GIRIS_INTRO_ANAHTAR, '1'); } catch { /* gizli pencere: açılışsız devam */ }
}

/** App kökünde durur: girişten sonraki ilk sayfa değişiminde işareti görürse açılışı sayfanın üstüne oynatır. */
export function GirisIntroKapisi() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [oyna, setOyna] = useState(false);
  // layout effect: yeni sayfa bir kare bile çıplak görünmesin
  useLayoutEffect(() => {
    let isaret = false;
    try {
      isaret = sessionStorage.getItem(GIRIS_INTRO_ANAHTAR) === '1';
      if (isaret) sessionStorage.removeItem(GIRIS_INTRO_ANAHTAR);
    } catch { /* yok say */ }
    if (isaret) setOyna(true);
  }, [pathname]);
  const bitti = useCallback(() => setOyna(false), []);
  if (!oyna) return null;
  const ad = String(user?.name || user?.ad_soyad || '').trim().split(/\s+/)[0] || '';
  return <GirisIntro ad={ad} onBitti={bitti} />;
}
